import React, { useEffect } from 'react';
import { Row, Col } from 'antd';
import {
  PhoneTwoTone, HourglassTwoTone, UpCircleTwoTone, ClockCircleTwoTone, ApiTwoTone,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Container } from './Styled';
import Line from '../chart/line/Line';
import Number from '../chart/number/Number';
import BuilderLayout from '../../layouts/builderLayout/BuilderLayout';
import Indicator from '../chart/indicator/Indicator';
import { RootState } from '../../../model/rootReducer';
import { timespanToString, timespanToTotalHour } from '../../../helpers/dateFormatHelper';
import { RangeTimeStatistic } from '../../../model/statistic/types';
import { getLiveStatisticAction, DateRange } from '../../../business/actions/statisticActions';

type DashboardProps = {
  className?: string;
};

export const Live: React.FunctionComponent<DashboardProps> = ({
  className,
}) => {
  const dateFormat = 'YYYY-MM-DD HH:mm ZZ';
  const dispatch = useDispatch();
  const statistic = useSelector(
    (state: RootState) => state.statistic.live,
  );
  const selectedBotId = useSelector((state: RootState) => state.bot.selectedBot);

  const refreshStatistic = () => {
    const endDate = moment().format(dateFormat);
    const startDate = moment().set({
      hour: 0, minute: 0, second: 0, millisecond: 0,
    }).format(dateFormat);
    dispatch(getLiveStatisticAction.request(new DateRange(startDate, endDate)));
  };

  useEffect(() => {
    refreshStatistic();
    const interval = setInterval(() => {
      refreshStatistic();
    }, 10000);
    return () => clearInterval(interval);
  }, [selectedBotId]);

  const formatRangeTimeStatistic = (statistics: RangeTimeStatistic[]) => {
    const datas = statistics.map((item, i) => ({
      x: `${moment(item.startTime, 'hh:mm:ss').format('HH:mm')}-${moment(item.endTime, 'hh:mm:ss').format('HH:mm')}`,
      y: item.callNumber,
    }));

    return [{
      id: 'rangeTimeStatistic',
      data: datas,
    }];
  };

  const StatContent = () => (
    <>
      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Number
            color={{
              '0%': '#108ee9',
              '100%': '#ab2cf4',
            }}
            title="En communication"
            text={statistic?.activeCall.toString()}
            percent={100}
            Icon={(
              <PhoneTwoTone
                style={{ fontSize: '30px' }}
                twoToneColor="#ab2cf4"
              />
          )}
          />
        </Col>
        <Col span={6}>
          <Number
            color={{
              '0%': '#4DEB62',
              '100%': '#3BC486',
            }}
            title="Taux de décroché"
            text="100"
            percent={100}
            subText="%"
            Icon={(
              <ApiTwoTone
                style={{ fontSize: '30px' }}
                twoToneColor="#3BC486"
              />
          )}
          />
        </Col>
        <Col span={6}>
          <Number
            color={{
              '0%': '#3426D1',
              '100%': '#7049E6',
            }}
            title="Durée moyenne de traitement"
            text={timespanToString(statistic?.statistics.dmt.value ?? '')}
            subText="min"
            percent={100}
            Icon={(
              <ClockCircleTwoTone
                style={{ fontSize: '30px' }}
                twoToneColor="#7049E6"
              />
          )}
            ExtraInformation={<Indicator text={timespanToString(statistic!.statistics.dmt.diff)} isIncrease={moment.duration(statistic!.statistics.dmt.diff).asMilliseconds() >= 0} />}
          />
        </Col>
        <Col span={6}>
          <Number
            color={{
              '0%': '#108ee9',
              '100%': '#87D9FA',
            }}
            title="Temps de communication"
            text={timespanToTotalHour(statistic?.statistics.totalCommunicationTime.value)}
            subText="heure(s)"
            percent={100}
            Icon={(
              <HourglassTwoTone
                style={{ fontSize: '30px' }}
                twoToneColor="#108ee9"
              />
          )}
            ExtraInformation={<Indicator text={timespanToString(statistic?.statistics.totalCommunicationTime.diff ?? '')} isIncrease={moment.duration(statistic!.statistics.totalCommunicationTime.diff).asMilliseconds() >= 0} />}
          />
        </Col>

      </Row>
      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Number
                color={{
                  '0%': '#108ee9',
                  '100%': '#87D9FA',
                }}
                title="Appels transférés"
                text={statistic?.statistics?.totalCallTransferred.value.toString()}
                percent={100}
                Icon={(
                  <UpCircleTwoTone
                    style={{ fontSize: '30px' }}
                    twoToneColor="#108ee9"
                  />
                )}
                ExtraInformation={<Indicator text={statistic?.statistics?.totalCallTransferred.diff.toString() ?? ''} isIncrease={statistic!.statistics?.totalCallTransferred.diff >= 0} />}
              />
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Number
                color={{
                  '0%': '#E03D5D',
                  '100%': '#E6397E',
                }}
                title="Appels abandonnés par l'appelant"
                text={statistic?.statistics?.totalCallHungupByUser.value.toString()}
                percent={100}
                Icon={(
                  <PhoneTwoTone
                    style={{ fontSize: '30px' }}
                    twoToneColor="#E03D5D"
                  />
          )}
                ExtraInformation={<Indicator text={statistic?.statistics?.totalCallHungupByUser.diff.toString() ?? ''} isIncrease={statistic!.statistics?.totalCallHungupByUser.diff >= 0} />}
              />
            </Col>
          </Row>
        </Col>
        <Col span={18}>
          <Line title="Volume d'appels par tranches horaires" color="#b61ff2" data={formatRangeTimeStatistic(statistic!.statistics.hourlyStatistic)} />
        </Col>
      </Row>
    </>
  );

  return (
    <BuilderLayout
      onBackDestination="/"
      title="Tableau de bord"
      subTitle={"Suivez l'activité de vos agents conversationnels en temps réel"}
    >
      <Container className={className}>
        {
            statistic ? <StatContent /> : null
          }
      </Container>
    </BuilderLayout>
  );
};

export default Live;
